import React, { useCallback, useId, useRef, useState } from 'react';
import {
  Upload,
  X,
  File,
  Image as ImageIcon,
  FileText,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { cn } from '../../lib/utils';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onUpload?: (file: File) => Promise<string>;
  accept?: string;
  maxSize?: number; // in bytes
  multiple?: boolean;
  className?: string;
  disabled?: boolean;
}

interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  uploading: boolean;
  url?: string;
  error?: string | null;
}

let fileCounter = 0;

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 bytes';
  const k = 1024;
  const sizes = ['bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  onUpload,
  accept = '*/*',
  maxSize = 10 * 1024 * 1024, // 10MB default
  multiple = false,
  className,
  disabled = false,
}) => {
  const inputId = useId();
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  // dragenter/dragleave fire for every child element; counting keeps the
  // drop zone from flickering as the pointer crosses the inner label.
  const dragDepth = useRef(0);

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return ImageIcon;
    if (fileType.includes('pdf') || fileType.includes('document')) return FileText;
    return File;
  };

  const updateFile = useCallback(
    (id: string, patch: Partial<UploadedFile>) => {
      setFiles((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...patch } : item))
      );
    },
    []
  );

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxSize) {
        return `is ${formatFileSize(file.size)}, over the ${formatFileSize(maxSize)} limit`;
      }
      if (accept !== '*/*') {
        const acceptedTypes = accept.split(',').map((t) => t.trim());
        const fileExtension = '.' + file.name.split('.').pop();
        const fileType = file.type;

        const isAccepted = acceptedTypes.some((type) => {
          if (type.startsWith('.')) {
            return fileExtension.toLowerCase() === type.toLowerCase();
          }
          return fileType.match(type.replace('*', '.*'));
        });

        if (!isAccepted) {
          return `is not an accepted file type (${accept})`;
        }
      }
      return null;
    },
    [maxSize, accept]
  );

  const handleFiles = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;

      const accepted: UploadedFile[] = [];
      const refusals: string[] = [];

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const validationError = validateFile(file);

        if (validationError) {
          refusals.push(`${file.name} ${validationError}.`);
          continue;
        }

        onFileSelect(file);
        accepted.push({
          id: `upload-${(fileCounter += 1)}`,
          file,
          uploading: !!onUpload,
          error: null,
        });
      }

      setRejected(refusals);
      if (accepted.length === 0) return;
      setFiles((prev) => [...prev, ...accepted]);

      for (const entry of accepted) {
        if (entry.file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) =>
            updateFile(entry.id, { preview: e.target?.result as string });
          reader.readAsDataURL(entry.file);
        }

        if (onUpload) {
          try {
            const url = await onUpload(entry.file);
            updateFile(entry.id, { url, uploading: false, error: null });
          } catch (err) {
            updateFile(entry.id, {
              uploading: false,
              error:
                err instanceof Error
                  ? err.message
                  : 'Upload failed. Check your connection and try again.',
            });
          }
        }
      }
    },
    [onFileSelect, onUpload, validateFile, updateFile]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter') {
      dragDepth.current += 1;
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth.current = 0;
      setDragActive(false);
      if (disabled) return;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles, disabled]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
      }
      // Allow re-selecting the same file after a removal.
      e.target.value = '';
    },
    [handleFiles]
  );

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <div className={cn('space-y-4', className)}>
      <div
        className={cn(
          'relative rounded-lg border-2 border-dashed p-8 transition-colors',
          dragActive ? 'border-primary bg-primary/5' : 'border-gray-300',
          disabled && 'cursor-not-allowed opacity-50'
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {/* sr-only rather than hidden: display:none removes the input from the
            tab order, which leaves keyboard users with no way to upload. */}
        <input
          type="file"
          id={inputId}
          className="peer sr-only"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          disabled={disabled}
        />

        <label
          htmlFor={inputId}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center rounded-md',
            'peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-4',
            disabled && 'cursor-not-allowed'
          )}
        >
          <Upload className="mb-4 h-12 w-12 text-gray-400" aria-hidden="true" />
          <span className="mb-1 text-sm font-medium text-gray-700">
            Drop {multiple ? 'files' : 'a file'} here, or click to browse
          </span>
          <span className="text-xs text-muted-foreground">
            {accept === '*/*' ? 'Any file type' : accept} &middot; up to{' '}
            {formatFileSize(maxSize)}
          </span>
        </label>
      </div>

      {rejected.length > 0 && (
        <div
          role="alert"
          className="flex gap-2 rounded-md border border-error bg-error/5 p-3"
        >
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-error"
            aria-hidden="true"
          />
          <div className="space-y-1">
            {rejected.map((message) => (
              <p key={message} className="text-sm text-error">
                {message}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((uploadedFile) => {
            const Icon = getFileIcon(uploadedFile.file.type);
            return (
              <li key={uploadedFile.id}>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-4">
                      {uploadedFile.preview ? (
                        <img
                          src={uploadedFile.preview}
                          alt=""
                          className="h-12 w-12 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-gray-100">
                          <Icon
                            className="h-6 w-6 text-gray-400"
                            aria-hidden="true"
                          />
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {uploadedFile.file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(uploadedFile.file.size)}
                        </p>

                        {uploadedFile.uploading && (
                          <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                            <Spinner size="sm" className="h-3 w-3 border" aria-hidden="true" />
                            Uploading&hellip;
                          </p>
                        )}

                        {uploadedFile.error && (
                          <p className="mt-1 text-xs text-error" role="alert">
                            {uploadedFile.error}
                          </p>
                        )}

                        {uploadedFile.url && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-success">
                            <CheckCircle2
                              className="h-3 w-3 shrink-0"
                              aria-hidden="true"
                            />
                            Uploaded
                          </p>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(uploadedFile.id)}
                        aria-label={`Remove ${uploadedFile.file.name}`}
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
